import { StatusChip, type ChipTone } from '@/design';

import type { RegistrationNumber, RegistrationRunStatus } from '@/domains/auth';

/**
 * A registration run's status, as the wire spells it — `in_progress`, `completed`,
 * `abandoned` — on the person hub's Registrations and on every row of the registrations queue,
 * one rendering for both so a run reads the same wherever staff meet it.
 *
 * The code rather than words, like a participant's kind on People and the hub: it is the
 * vocabulary the queue's filter is ticked in, and a label that differed from the checkbox beside
 * it would be two names for one state. Only the tone is this app's, one table for the
 * vocabulary (the design system's `StatusChip`): `completed` settled, `in_progress` under way,
 * `abandoned` a plain state — an outcome to read, not an alarm — and a status survey-api adds
 * later still renders, neutral, in its own word.
 */
export function RunStatusBadge({ status }: { status: RegistrationRunStatus }) {
  return (
    <StatusChip tone={RUN_STATUS_TONE[status] ?? 'neutral'} dot>
      {status}
    </StatusChip>
  );
}

const RUN_STATUS_TONE: Partial<Record<string, ChipTone>> = {
  completed: 'success',
  in_progress: 'info',
  abandoned: 'neutral'
};

/**
 * A person's run (SR-487) has no survey: where a survey's run names one, it reads "Person" and
 * the numbers it declared — `Person — CRD, Insurance`, `Person — No number` for none — on the
 * queue and the hub alike. Null for a survey's run (`numbers` null, or absent from an API before
 * SR-487), which keeps its survey. Only the number in hand is on the row: survey-api drops it
 * once the run holds it, so a person run past its numbers draws no number and no registry name.
 */
export function personRunLabel(numbers: Nullable<RegistrationNumber[]> | undefined) {
  if (!numbers) return null;
  if (numbers.length === 0) return 'Person — No number';
  return `Person — ${numbers.map((number) => NUMBER_WORDS[number] ?? number).join(', ')}`;
}

const NUMBER_WORDS: Partial<Record<string, string>> = { crd: 'CRD', insurance: 'Insurance' };
